import Tabs, { TabsProps } from "components/basic/tabs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { Box, IconButton, useTheme } from "@mui/material";
import { useMessageNavigation } from "../message/messagesApi";
import QuoteTable from "./quotesTable";
import {
  QuoteApprovalStatus,
  useDeleteQuoteMutation,
  useGetQuotesByAccountQuery,
  useMeQuery,
  useRespondToQuoteMutation,
  useUpdateMyQuoteMutation,
} from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import { useQuoteCreated } from "hooks/useQuoteCreated";
import { useQuoteUpdated } from "hooks/useQuoteUpdated";
import useUrlParamState from "hooks/useUrlParamState";
import Button from "components/basic/button";
import Badge from "components/basic/badge";
import type { GoogleRatingListing } from "components/basic/googleRating";
import Quote from "pages/chargerEstimator/formPages/quote";
import {
  addOnLabels,
  buildQuotePageDataFromQuote,
} from "pages/chargerEstimator/formPages/quote/quoteTools";
import BookAppointmentModal from "../calendar/booking/components/BookAppointmentModal";
import { quoteLabel } from "../calendar/booking/components/BookAppointmentModal/bookAppointmentApi";
import Typography from "components/basic/typography";
import { useTranslation } from "react-i18next";
import { useFormat } from "hooks/useFormat";
import { formatExpiry } from "./expiryHelpers";
import { quoteStatusMeta } from "./quoteStatus";
import RequestChangesModal from "./components/RequestChangesModal";
import {
  EMPTY_TAB_TEXT,
  isOnTab,
  isQuoteTab,
  QUOTE_TABS,
  QuoteTab,
} from "./helpers";

export interface QuoteRow {
  id: number;
  vendorId: number;
  vendor: string;
  googleListing: GoogleRatingListing | null;
  isVerified: boolean;
  distance: string;
  service: string;
  detail: string;
  cost: string;
  dateUpdated: string;
  expiresAt?: string | null;
  isExpired: boolean;
  approvalStatus: QuoteApprovalStatus;
  starred: boolean;
  archived: boolean;
  viewed: boolean;
}

type QuoteEdit = Partial<Pick<QuoteRow, "starred" | "archived" | "viewed">>;

const Quotes = () => {
  const theme = useTheme();
  const { t } = useTranslation("quotes");
  const format = useFormat();
  const [tabParam, setTabParam] = useUrlParamState("tab", "all");
  const selectedTab: QuoteTab = isQuoteTab(tabParam) ? tabParam : "all";
  const showSnack = useSnack();

  const [{ data }, refetch] = useGetQuotesByAccountQuery({
    requestPolicy: "cache-and-network",
  });
  useQuoteCreated(refetch);
  useQuoteUpdated(refetch);
  const [{ data: meData }] = useMeQuery();
  const [, deleteQuote] = useDeleteQuoteMutation();
  const [, updateMyQuote] = useUpdateMyQuoteMutation();
  const [{ fetching: responding }, respondToQuote] =
    useRespondToQuoteMutation();
  const handleMessage = useMessageNavigation();

  // Which saved quote (if any) is open in the read-only detail view.
  const [selectedQuoteId, setSelectedQuoteId] = useUrlParamState("quoteId");
  const [bookingOpen, setBookingOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);

  const rawQuotes = useMemo(() => data?.getQuotesByAccount ?? [], [data]);

  // Raw record behind the detail view (vendor id/name for the action buttons).
  const selectedQuote = useMemo(
    () => rawQuotes.find((q) => `${q.id}` === selectedQuoteId),
    [rawQuotes, selectedQuoteId],
  );

  const savedQuotes: QuoteRow[] = useMemo(
    () =>
      rawQuotes.map((q) => ({
        id: q.id,
        vendorId: q.evChargerService.vendor.id,
        vendor: q.evChargerService.vendor.businessName,
        googleListing: q.evChargerService.vendor.googleListing ?? null,
        isVerified: q.evChargerService.vendor.isVerified,
        distance: q.distance,
        service: t("service_ev_charger"),
        detail: q.addOns.length
          ? `${q.chargerModel}, ${addOnLabels(q.addOns)}`
          : q.chargerModel,
        cost: format.money(q.totalCost),
        dateUpdated: q.updatedAt,
        expiresAt: q.expiresAt,
        isExpired: q.isExpired,
        approvalStatus: q.approvalStatus,
        starred: Boolean(q.starred),
        archived: Boolean(q.archived),
        viewed: Boolean(q.viewed),
      })),
    [rawQuotes, t, format],
  );

  const [pendingEdits, setPendingEdits] = useState<Record<number, QuoteEdit>>(
    {},
  );

  const quotes = useMemo(
    () =>
      savedQuotes.map((quote) =>
        pendingEdits[quote.id]
          ? { ...quote, ...pendingEdits[quote.id] }
          : quote,
      ),
    [savedQuotes, pendingEdits],
  );

  // Shown at once, then either confirmed by the server's copy or dropped.
  const applyEdit = useCallback(
    async (quoteId: number, patch: QuoteEdit) => {
      setPendingEdits((prev) => ({
        ...prev,
        [quoteId]: { ...prev[quoteId], ...patch },
      }));

      const result = await updateMyQuote({ id: quoteId, ...patch });
      if (result.error || !result.data?.updateMyQuote) {
        showSnack(t("snack.update_failed"), "error");
      }

      setPendingEdits(({ [quoteId]: _confirmed, ...rest }) => rest);
    },
    [updateMyQuote, showSnack, t],
  );

  const tabQuotes = useMemo(
    () => quotes.filter((quote) => isOnTab(quote, selectedTab)),
    [quotes, selectedTab],
  );
  const unprocessedCount = useMemo(
    () => quotes.filter((quote) => isOnTab(quote, "unprocessed")).length,
    [quotes],
  );

  const toggleStar = (quote: QuoteRow) =>
    applyEdit(quote.id, { starred: !quote.starred });
  const toggleArchive = (quote: QuoteRow) => {
    applyEdit(quote.id, { archived: !quote.archived });
    showSnack(
      t(quote.archived ? "snack.restored" : "snack.archived"),
      "success",
    );
  };

  const detailRow = quotes.find((quote) => `${quote.id}` === selectedQuoteId);

  // Opening a quote takes it off the Unprocessed tab. Tried once per quote, so
  // a failed save can't retry on every render.
  const markedViewed = useRef(new Set<number>());
  useEffect(() => {
    if (
      detailRow &&
      !detailRow.viewed &&
      !markedViewed.current.has(detailRow.id)
    ) {
      markedViewed.current.add(detailRow.id);
      applyEdit(detailRow.id, { viewed: true });
    }
  }, [detailRow, applyEdit]);

  // Build read-only QuotePageData for the selected saved quote.
  const selectedQuoteData = useMemo(() => {
    if (!selectedQuote) {
      return null;
    }
    const me = meData?.me;
    return buildQuotePageDataFromQuote(selectedQuote, {
      fullName: me ? `${me.firstName} ${me.lastName}` : "",
      email: me?.email ?? "",
      phoneNumber: me?.phoneNumber ?? "",
    });
  }, [selectedQuote, meData]);

  const handleDelete = async (id: number) => {
    const result = await deleteQuote({ id });
    if (result.data?.deleteQuote) {
      showSnack(t("snack.deleted"), "success");
      refetch({ requestPolicy: "network-only" });
    } else {
      showSnack(t("snack.delete_failed"), "error");
    }
  };

  // Approve the quote, or send it back to the vendor with what to change.
  const handleRespond = async (
    id: number,
    response: "approve" | "request_changes",
    note?: string,
  ) => {
    const result = await respondToQuote({ id, response, note });
    const payload = result.data?.respondToQuote;

    if (payload?.quote) {
      showSnack(
        t(response === "approve" ? "snack.approved" : "snack.changes_sent"),
        "success",
      );
      setChangesOpen(false);
      refetch({ requestPolicy: "network-only" });
      return;
    }

    showSnack(
      payload?.errors?.[0]?.message ?? t("snack.respond_failed"),
      "error",
    );
  };

  const tabData: TabsProps = {
    selectedTab,
    setSelectedTab: setTabParam,
    tabs: QUOTE_TABS.map((tab) => ({
      ...tab,
      label:
        tab.path === "unprocessed" && unprocessedCount
          ? `${t(tab.label)} (${unprocessedCount})`
          : t(tab.label),
    })),
  };

  // Read-only detail view for a saved quote.
  if (selectedQuoteId && selectedQuoteData && selectedQuote) {
    const vendorId = selectedQuote.evChargerService.vendor.id;
    const vendorName = selectedQuote.evChargerService.vendor.businessName;
    const status = quoteStatusMeta(selectedQuote.approvalStatus, "customer");
    // An expired quote's pricing is off the table, so there is nothing left to
    // answer — the server refuses a response to one too.
    const canRespond =
      !selectedQuote.isExpired &&
      selectedQuote.approvalStatus !== QuoteApprovalStatus.Approved;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            padding: theme.spacing(1),
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Button
            variant="text"
            startAdornment={<ArrowBackIcon />}
            onClick={() => setSelectedQuoteId(null)}
          >
            {t("actions.back")}
          </Button>
          <Box
            sx={{
              display: "flex",
              gap: theme.spacing(1),
              alignItems: "center",
              flexWrap: "wrap",
              // Four buttons plus a badge need ~700px, so on a phone they
              // split the row evenly instead of running off the end of it.
              "& > .MuiButton-root": {
                flex: { xs: "1 1 0", sm: "0 0 auto" },
              },
            }}
          >
            {selectedQuote.isExpired && (
              <Typography
                variant="body2"
                color="error"
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                {t("detail.expired_notice", {
                  date: formatExpiry(
                    t,
                    format.date,
                    selectedQuote.expiresAt,
                    true,
                  ).date,
                })}
              </Typography>
            )}
            <Badge label={t(status.label)} color={status.color} />
            {detailRow && (
              <IconButton
                aria-label={t(
                  detailRow.starred ? "actions.unstar" : "actions.star",
                )}
                onClick={() => toggleStar(detailRow)}
              >
                {detailRow.starred ? (
                  <StarIcon color="warning" />
                ) : (
                  <StarBorderIcon />
                )}
              </IconButton>
            )}
            {detailRow && (
              <Button onClick={() => toggleArchive(detailRow)}>
                {t(
                  detailRow.archived ? "actions.unarchive" : "actions.archive",
                )}
              </Button>
            )}
            <Button onClick={() => handleMessage(vendorId)}>
              {t("actions.message_vendor")}
            </Button>
            {canRespond && (
              <Button
                onClick={() => setChangesOpen(true)}
                disabled={responding}
              >
                {t("actions.request_changes")}
              </Button>
            )}
            {canRespond && (
              <Button
                variant="contained"
                color="success"
                loading={responding}
                onClick={() => handleRespond(selectedQuote.id, "approve")}
              >
                {t("actions.approve")}
              </Button>
            )}
            {/* An expired quote's pricing is no longer on offer — the server
                rejects a booking against it too. */}
            <Button
              variant="contained"
              onClick={() => setBookingOpen(true)}
              disabled={selectedQuote.isExpired}
            >
              {t("actions.book")}
            </Button>
          </Box>
        </Box>
        {selectedQuote.approvalStatus ===
          QuoteApprovalStatus.ChangesRequested &&
          selectedQuote.changeRequestNote && (
            <Typography
              variant="body2"
              style={{
                padding: theme.spacing(0, 1, 1),
                color: theme.palette.text.secondary,
              }}
            >
              {t("detail.you_asked", { note: selectedQuote.changeRequestNote })}
            </Typography>
          )}
        <div style={{ flex: 1, minHeight: 0 }}>
          <Quote quoteData={selectedQuoteData} readOnly height="100%" />
        </div>
        {bookingOpen && (
          <BookAppointmentModal
            vendors={[
              {
                vendorId,
                vendorName,
                quotes: [
                  {
                    quoteId: selectedQuote.id,
                    label: quoteLabel(selectedQuote),
                    expiresAt: selectedQuote.expiresAt,
                  },
                ],
              },
            ]}
            onClose={() => setBookingOpen(false)}
          />
        )}
        {changesOpen && (
          <RequestChangesModal
            vendorName={vendorName}
            estimateNumber={selectedQuote.estimateNumber}
            submitting={responding}
            onSubmit={(note) =>
              handleRespond(selectedQuote.id, "request_changes", note)
            }
            onClose={() => setChangesOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Tabs {...tabData} />
      <div>
        <QuoteTable
          quotes={tabQuotes}
          emptyText={t(EMPTY_TAB_TEXT[selectedTab])}
          onDelete={handleDelete}
          onView={(id) => setSelectedQuoteId(`${id}`)}
          onMessage={handleMessage}
          onToggleStar={toggleStar}
          onToggleArchive={toggleArchive}
        />
      </div>
    </div>
  );
};

export default Quotes;
